import React, { useRef } from "react";
import { motion, useScroll, useTransform, useSpring, useMotionValue } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { MaskLine, EASE } from "./Reveal";
import { IMAGES } from "@/constants/site";

export const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);
  const contentFade = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  const rx = useSpring(useMotionValue(0), { stiffness: 55, damping: 16 });
  const ry = useSpring(useMotionValue(0), { stiffness: 55, damping: 16 });

  const onMouseMove = (e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * 6);
    rx.set(-py * 4);
  };
  const onMouseLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      className="relative min-h-[100svh] bg-burgundy flex items-end sm:items-center overflow-hidden"
    >
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Calice di vino rosso nell'atmosfera della cantina"
          className="w-full h-full object-cover"
          loading="eager"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#240409]/95 via-[#240409]/70 to-[#240409]/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#240409] via-transparent to-[#240409]/50" />

      <motion.div
        style={{ opacity: contentFade }}
        className="relative z-10 w-full max-w-[1200px] mx-auto px-6 pb-24 sm:pb-28 pt-32"
      >
        <div className="flex flex-wrap items-end justify-between gap-10">
          <div className="max-w-3xl">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
              className="text-xs font-mono uppercase tracking-[0.3em] text-gold"
            >
              {t.hero.eyebrow}
            </motion.p>

            <h1 className="mt-6 font-serif font-semibold text-cream leading-[0.95] tracking-tight text-[clamp(3.2rem,10vw,7.5rem)]">
              <MaskLine delay={0.25}>{t.hero.titleA}</MaskLine>
              <MaskLine delay={0.4} className="italic text-gold">
                {t.hero.titleB}
              </MaskLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.65, ease: EASE }}
              className="mt-6 text-base sm:text-lg lg:text-xl font-light text-cream/80 tracking-wide leading-relaxed"
            >
              {t.hero.subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.8, ease: EASE }}
              className="mt-9 flex flex-wrap gap-4"
            >
              <motion.a
                href="#prenota"
                data-testid="hero-cta-booking-button"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-burgundy shadow-[0_0_25px_rgba(198,156,78,0.25)] hover:bg-amberwarm transition-colors"
              >
                {t.hero.ctaBooking}
              </motion.a>
              <motion.a
                href="#menu"
                data-testid="hero-cta-menu-button"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center rounded-full border border-cream/40 px-7 py-3.5 text-sm font-medium text-cream hover:bg-cream/10 transition-colors"
              >
                {t.hero.ctaMenu}
              </motion.a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1, ease: EASE }}
            style={{ rotateX: rx, rotateY: ry, transformPerspective: 800 }}
            className="hidden lg:block w-72 rounded-2xl border border-gold/40 bg-cream/10 backdrop-blur-md p-6 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.4)]"
          >
            <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-gold">{t.hero.cardTitle}</p>
            <ul className="mt-4 space-y-3">
              {t.hero.cardLines.map((line) => (
                <li key={line} className="flex items-center gap-3 text-sm text-cream/85">
                  <span className="w-1.5 h-1.5 rounded-full bg-gold shrink-0" />
                  {line}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </motion.div>

      <motion.a
        href="#chi-siamo"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-cream/60 hover:text-gold transition-colors"
        data-testid="hero-scroll-indicator"
      >
        <span className="text-[10px] font-mono uppercase tracking-[0.3em]">{t.hero.scroll}</span>
        <ArrowDown className="w-4 h-4 animate-bounce" />
      </motion.a>
    </section>
  );
};
